import dayjs from "dayjs";
import type { Point as HighchartsPoint, Tooltip as HighchartsTooltip, TooltipPositionerPointObject } from "highcharts";
import ReactDOMServer from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";

import type { StructuredChartDataSchema } from "app/pages/benchmark-tracker/hooks/types";
import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import { BenchmarkMetricName } from "app/pages/benchmark-tracker/types/api.types";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";

import { getTooltip, tooltipPositioner } from "./helpers";
import { StyledChangeValue, StyledMarker } from "./styles";

const TooltipTitles = {
    [BenchmarkMetricName.visits]: `bt.metrics.tooltip.title.value.${BenchmarkMetricName.visits}`,
    [BenchmarkMetricName.visitDuration]: `bt.metrics.tooltip.title.value.${BenchmarkMetricName.visitDuration}`,
    [BenchmarkMetricName.bounceRate]: `bt.metrics.tooltip.title.value.${BenchmarkMetricName.bounceRate}`,
    [BenchmarkMetricName.pagesPerVisit]: `bt.metrics.tooltip.title.value.${BenchmarkMetricName.pagesPerVisit}`,
    [BenchmarkMetricName.pageViews]: `bt.metrics.tooltip.title.value.${BenchmarkMetricName.pageViews}`,
};

export const getBarChartConfig = (
    granularity: BenchmarkGranularity,
    graphData: Array<StructuredChartDataSchema>,
    isDifference: boolean,
    formatter: (val: number, decimal?: number) => string,
    isInverted: boolean,
    metricName: BenchmarkMetricName,
    dates: string[],
    isRelative?: boolean,
) => {
    const isWeekly = granularity === BenchmarkGranularity.WEEKLY;

    const xAxisFormatter = ({ value }: { value: number }) => dayjs.utc(value).format(isWeekly ? "MMM DD" : "MMM YY");
    const yAxisFormatter = ({ value }: { value: number }) => {
        if (value === 0 && isDifference) {
            return ReactDOMServer.renderToString(<StyledMarker backgroundColor={colorsPalettes.blue[200]} />);
        }

        return formatter(value);
    };

    return combineConfigs(
        {
            xAxisFormatter,
            yAxisFormatter,
            data: graphData,
        },
        [
            {},
            noLegendConfig,
            tooltipContainerConfig,
            xAxisLabelsConfig,
            xAxisCrosshair,
            {
                chart: {
                    height: 260,
                    type: "column",
                },
                tooltip: {
                    followPointer: false,
                    shared: true,
                    outside: true,
                    useHTML: true,
                    positioner: function (
                        this: HighchartsTooltip,
                        width: number,
                        height: number,
                        point: TooltipPositionerPointObject,
                    ) {
                        return tooltipPositioner(width, height, point, isDifference, this);
                    },
                    formatter: function (this: HighchartsPoint) {
                        const tooltipMetricNameTitle = TooltipTitles[metricName as keyof typeof TooltipTitles];

                        return getTooltip(
                            isWeekly,
                            this,
                            isDifference,
                            formatter,
                            isInverted,
                            tooltipMetricNameTitle,
                            isRelative,
                        );
                    },
                },
                yAxis: {
                    gridLineWidth: 0.5,
                    reversed: false,
                    gridZIndex: 2,
                    endOnTick: true,
                    labels: {
                        useHTML: true,
                        formatter: yAxisFormatter,
                        style: {
                            textTransform: "uppercase",
                            fontSize: "11px",
                            fontFamily: "Roboto",
                            color: colorsPalettes.carbon[200],
                        },
                    },
                    plotLines: isDifference && [
                        {
                            value: 0,
                            color: colorsPalettes.carbon[200],
                            width: 1,
                            zIndex: 3,
                        },
                    ],
                },
                xAxis: {
                    categories: dates,
                    gridLineWidth: 0,
                    lineColor: isDifference ? colorsPalettes.carbon[50] : colorsPalettes.carbon[200],
                    labels: {
                        style: {
                            textTransform: "capitalize",
                            fontSize: "11px",
                            color: "#919191",
                            fontFamily: "Roboto",
                            textOverflow: "none",
                            whiteSpace: "nowrap",
                        },
                    },
                    zoomEnabled: false,
                },
                plotOptions: {
                    area: {
                        stacking: "normal",
                        animation: false,
                    },
                    series: {
                        maxPointWidth: isDifference ? 20 : 60,
                        pointPadding: 0.2,
                        marker: {
                            enabled: true,
                            symbol: "circle",
                        },
                        stacking: undefined,
                        animation: false,
                        dataLabels: {
                            enabled: isDifference,
                            useHTML: true,
                            crop: false,
                            overflow: "allow",
                            formatter(this: HighchartsPoint) {
                                const isDecrease = (this.y ?? 0) < 0;
                                const convertToPlusValues = (this.y ?? 0) < 0 ? -(this.y ?? 0) : (this.y ?? 0);

                                return ReactDOMServer.renderToString(
                                    <StyledChangeValue
                                        value={formatter(convertToPlusValues)}
                                        isDecrease={isInverted ? !isDecrease : isDecrease}
                                        invertColors={isInverted}
                                    />,
                                );
                            },
                        },
                    },
                },
            },
        ],
    );
};
